// layouts/autenticacion/usuarios/index.js
import { useAuth } from "layouts/auth/AuthContext";
import { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

import ListPage from "ui/ListPage";
import ConfirmDialog from "ui/ConfirmDialog";
import SnackbarAlert from "ui/SnackbarAlert";

import { listUsers, deleteUser } from "layouts/autenticacion/usuarios/services/users";
import buildUsuariosTableData, {
  columns as userCols,
} from "layouts/autenticacion/usuarios/dataUsuarios";
import api from "services/api";

const toArray = (v) => (Array.isArray(v) ? v : []);
const errMsg = (e) =>
  e?.response?.data?.message ||
  (Array.isArray(e?.response?.data)
    ? e.response.data.map((x) => x?.message || x).join(", ")
    : null) ||
  e?.message ||
  "Error";

/* ========= BUSCADOR =========
   - Texto libre -> OR “manual” combinando resultados de:
       username[$contains], name[$contains], email[$contains]
   - clave:valor soportadas:
     usuario/username, email/correo, nombre/name
*/
const CONTAINS_FIELDS = ["name", "email"];
const MIN_SEARCH_LENGTH = 2;
const MAX_SEARCH_LENGTH = 80;
const EXACT_FIELDS = ["group_id", "id", "_id", "is_admin", "disabled"];

const SEARCH_PARAM_MAP = {
  nombre: "name",
  name: "name",
  correo: "email",
  email: "email",
};

const normalizeBool = (v) => /^(1|true|sí|si|yes|y|on)$/i.test(String(v));
const normalizeStatusKV = (v) => {
  const s = String(v).toLowerCase().trim();
  if (/(activo|active|0)$/i.test(s)) return { disabled: 0 };
  if (/(suspendido|suspended|1)$/i.test(s)) return { disabled: 1 };
  return {};
};

function normalizeSearchText(input = "") {
  return String(input || "")
    .normalize("NFKC")
    .replace(/[<>\[\]{}()\\`'";%]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_SEARCH_LENGTH);
}

function isSearchAllowed(input = "") {
  const s = normalizeSearchText(input);
  if (!s) return true;
  if (s.length < MIN_SEARCH_LENGTH) return false;

  // Evita búsquedas con puro símbolo: @, ., -, _, etc.
  return /[\p{L}\p{N}]/u.test(s);
}

// 🔧 SOLO para clave:valor (NO genera $or[])
function buildSearchParams(input = "") {
  const s = String(input || "").trim();
  const params = {};
  if (!s) return params;

  const tokens = s.match(/\S+:"[^"]+"|\S+:\S+|".+?"|\S+/g) || [];

  for (const tok of tokens) {
    const m = tok.match(/^([^:]+):(.+)$/);
    if (!m) continue;

    const rawKey = m[1].toLowerCase().trim();
    let rawVal = m[2].trim();
    if (rawVal.startsWith('"') && rawVal.endsWith('"')) rawVal = rawVal.slice(1, -1);
    const key = SEARCH_PARAM_MAP[rawKey] || rawKey;

    if (key === "status") {
      Object.assign(params, normalizeStatusKV(rawVal));
      continue;
    }
    if (key === "is_admin" || key === "disabled") {
      params[key] = normalizeBool(rawVal) ? 1 : 0;
      continue;
    }

    if (EXACT_FIELDS.includes(key)) {
      params[key] = rawVal;
      continue;
    }

    if (CONTAINS_FIELDS.includes(key)) {
      params[`${key}[$contains]`] = rawVal;
      continue;
    }

    // fallback: username contains
    params["username[$contains]"] = rawVal;
  }

  return params;
}

// Texto libre si NO hay pares clave:valor
function extractFreeText(input = "") {
  const s = normalizeSearchText(input);
  if (!s) return "";
  const hasPairs = /\b[^:\s]+:/.test(s);
  if (hasPairs) return "";
  return s.replace(/^"|"$/g, "");
}

// Dedupe por id preservando orden
function dedupeById(arr = []) {
  const seen = new Set();
  const out = [];
  for (const it of arr) {
    const id = it?.id ?? it?._id;
    if (id == null) continue;
    if (!seen.has(id)) {
      seen.add(id);
      out.push(it);
    }
  }
  return out;
}

async function fetchUsersByNameOrEmail({ query, need }) {
  const fields = ["name", "email"];
  const merged = [];

  // Secuencial a propósito: evita abrir varias consultas en paralelo contra la BD.
  for (const f of fields) {
    const res = await listUsers({
      __limit: need,
      __offset: 0,
      [`${f}[$contains]`]: query,
    });

    const rows = Array.isArray(res?.rows)
      ? res.rows
      : Array.isArray(res?.page)
      ? res.page
      : [];

    merged.push(...rows);
  }

  return dedupeById(merged);
}

export default function Usuarios() {
  const { user: authUser, isImpersonating, devImpersonateAs } = useAuth();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");

  // paginación server-side
  const [page, setPage] = useState(0); // 0-based
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  const [groupsMap, setGroupsMap] = useState({});

  const [pending, setPending] = useState(null);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "success" });
  const openSnack = (m, s = "success") => setSnack({ open: true, message: m, severity: s });
  const closeSnack = () => setSnack((s) => ({ ...s, open: false }));


  // fetch users con paginación + búsqueda
  const fetchList = useCallback(
    async ({ pageIndex = 0, pageSize = 10, currentSearch = "" } = {}) => {
      setLoading(true);
      setErr("");
      try {
        const limit = pageSize;
        const offset = pageIndex * pageSize;

        // Búsqueda de texto libre (sin clave:valor) -> múltiples llamadas con $contains
        const normalizedSearch = normalizeSearchText(currentSearch);

        if (!isSearchAllowed(normalizedSearch)) {
          setItems([]);
          setTotal(0);
          return;
        }

        // Búsqueda de texto libre: nombre o email.
        // Se ejecuta secuencialmente para no saturar la conexión de BD.
        const freeQ = extractFreeText(normalizedSearch);
        if (freeQ) {
          const need = Math.min((pageIndex + 1) * pageSize, 200);
          const merged = await fetchUsersByNameOrEmail({ query: freeQ, need });
          const pageSlice = merged.slice(offset, offset + limit);

          setItems(pageSlice);
          setTotal(merged.length);
          return;
        }

        // Búsqueda avanzada (clave:valor) -> sin $or[], sólo campo[$contains]/exactos
        const params = buildSearchParams(currentSearch);
        const res = await listUsers({ __limit: limit, __offset: offset, ...params });

        setItems(res.rows || []);
        setTotal(Number(res.total || 0));
      } catch (e) {
        const msg = errMsg(e);

        // Si backend truena con un error de SQL por "IN ()" => tratamos como 0 resultados
        if (
          typeof msg === "string" &&
          (msg.includes("SQL syntax") || msg.includes("near '0'"))
        ) {
          setItems([]);
          setTotal(0);
          setErr(""); // no mostrar alerta
        } else {
          setItems([]);
          setTotal(0);
          setErr(msg);
        }
      } finally {
        setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    if (!items.length) return;

    const missingGroupIds = new Set();

    items.forEach((it) => {
      const userGroups = Array.isArray(it.groups)
        ? it.groups
        : [];

      userGroups.forEach((g) => {
        const gid =
          typeof g === "object" && g !== null
            ? g.id ?? g._id
            : g;

        if (
          gid !== null &&
          gid !== undefined &&
          gid !== "" &&
          groupsMap[String(gid)] == null
        ) {
          missingGroupIds.add(String(gid));
        }
      });
    });

    if (!missingGroupIds.size) return;

    let cancel = false;

    (async () => {
      try {
        const groups = await Promise.all(
          Array.from(missingGroupIds).map(async (id) => {
            try {
              const { data } = await api.get(
                `/group/${encodeURIComponent(id)}`
              );

              return data?.data ?? data ?? null;
            } catch (e) {
              console.error(
                `[USUARIOS] No se pudo cargar grupo ${id}:`,
                e
              );

              return null;
            }
          })
        );

        if (cancel) return;

        setGroupsMap((prev) => {
          const next = { ...prev };

          groups.forEach((g) => {
            if (!g) return;

            const id = String(
              g.id ?? g._id ?? ""
            ).trim();

            if (!id) return;

            next[id] =
              g.name ??
              g.nombre ??
              g.title ??
              `Grupo ${id}`;
          });

          return next;
        });
      } catch (e) {
        console.error(
          "[USUARIOS] error resolviendo grupos:",
          e
        );
      }
    })();

    return () => {
      cancel = true;
    };
  }, [items, groupsMap]);


  // búsqueda con debounce
  useEffect(() => {
    const normalizedSearch = normalizeSearchText(search);

    if (!isSearchAllowed(normalizedSearch)) {
      setPage(0);
      setItems([]);
      setTotal(0);
      setErr(`Escribe al menos ${MIN_SEARCH_LENGTH} caracteres válidos para buscar.`);
      setLoading(false);
      return undefined;
    }

    const t = setTimeout(() => {
      setPage(0);
      fetchList({ pageIndex: 0, pageSize: rowsPerPage, currentSearch: normalizedSearch });
    }, 500);

    return () => clearTimeout(t);
  }, [search, rowsPerPage, fetchList]);

  // construir filas
  const { columns, rows } = useMemo(
    () =>
      buildUsuariosTableData(toArray(items), (it) => {
        const id = it.id ?? it._id;
        const username =
          it.username || (it.email ? String(it.email).split("@")[0] : `user_${id}`);
        const fullName =
          [it.name, it.first_surname].filter(Boolean).join(" ") || "—";
        const groupIds = Array.isArray(it.groups)
          ? it.groups.map((g) =>
              typeof g === "object" && g !== null
                ? String(g.id ?? g._id)
                : String(g)
            )
          : it.group_id != null
          ? [String(it.group_id)]
          : [];

        const groupName =
          groupIds
            .map((id) => groupsMap[id])
            .filter(Boolean)
            .join(", ") || "—";
        const canImpersonate =
          !!authUser?.is_admin && !isImpersonating && authUser?.id !== id;

        return {
          id,
          username,
          fullName,
          email: it.email ?? "—", // 👈 se usa en la tabla
          group: groupName,
          status: it.disabled ? "suspendido" : "activo",
          lastSession: it.created_at,
          onEdit: () => navigate(`/usuarios/${id}/editar`),
          onDelete: () =>
            setPending({
              id,
              label: username || fullName || it.email || `ID ${id}`,
            }),
          onImpersonate: canImpersonate
            ? async () => {
                try {
                  await devImpersonateAs(id);
                  navigate("/dashboard", { replace: true });
                } catch (e) {
                  console.error("[Impersonate] error:", e);
                }
              }
            : undefined,
        };
      }),
    [items, groupsMap, navigate, authUser, isImpersonating, devImpersonateAs]
  );

  // paginación
  const onPageChange = (_e, newPage) => {
    setPage(newPage);
    fetchList({ pageIndex: newPage, pageSize: rowsPerPage, currentSearch: search });
  };

  const onRowsChange = (e) => {
    const newSize = parseInt(e.target.value, 10);
    setRowsPerPage(newSize);
    setPage(0);
    fetchList({ pageIndex: 0, pageSize: newSize, currentSearch: search });
  };

  const confirmDelete = async () => {
    if (!pending) return;
    try {
      await deleteUser(pending.id);
      openSnack(`Eliminado: ${pending.label}`, "success");
      fetchList({ pageIndex: page, pageSize: rowsPerPage, currentSearch: search });
    } catch (e) {
      const m = errMsg(e);
      setErr(m);
      openSnack(`No se pudo eliminar: ${m}`, "error");
    } finally {
      setPending(null);
    }
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />

      <ListPage
        title="Usuarios"
        search={search}
        onSearchChange={setSearch}
        addText="Agregar"
        onAdd={() => navigate("/usuarios/nuevo")}
        columns={userCols}
        rows={rows}
        loading={loading}
        error={err}
        cardSx={{ maxWidth: "100%", mx: 0 }}
        paginationProps={
          !loading && {
            count: total,
            page,
            rowsPerPage,
            onPageChange,
            onRowsPerPageChange: onRowsChange,
            rowsPerPageOptions: [5, 10, 25, 50, 100],
            showFirstButton: true,
            showLastButton: true,
          }
        }
      />

      <Footer />

      <ConfirmDialog
        open={!!pending}
        title="Eliminar usuario"
        description={
          pending
            ? `¿Seguro que deseas eliminar "${pending.label}"? Esta acción no se puede deshacer.`
            : ""
        }
        confirmText="Eliminar"
        onClose={() => setPending(null)}
        onConfirm={confirmDelete}
      />

      <SnackbarAlert
        open={snack.open}
        onClose={closeSnack}
        message={snack.message}
        severity={snack.severity}
      />
    </DashboardLayout>
  );
}
